import React, { useEffect, useReducer } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition, IconPrefix } from '@fortawesome/fontawesome-common-types';
import {
  faArrowRotateLeft,
  faArrowsLeftRight,
  faArrowsTurnToDots,
  faArrowTrendUp,
  faBadgeCheck,
  faBook,
  faBookmark,
  faBox,
  faBoxCheck,
  faBoxesStacked,
  faBrain,
  faBullseye,
  faBullseyeArrow,
  faCalendar,
  faCalendarCheck,
  faCalendarClock,
  faCalendarWeek,
  faCar,
  faCartShopping,
  faCashRegister,
  faChartArea,
  faChartBar,
  faChartLine,
  faChartMixed,
  faChartSimple,
  faChess,
  faCircleCheck,
  faCircleQuestion,
  faClipboardCheck,
  faClock,
  faComments,
  faCompass,
  faCreditCard,
  faCrystalBall,
  faCubes,
  faDatabase,
  faDiagramNested,
  faDollarSign,
  faEye,
  faFaceSmile,
  faFile,
  faFileInvoice,
  faFileLines,
  faFilePowerpoint,
  faFlag,
  faFolderTree,
  faGaugeHigh,
  faGear,
  faGears,
  faGlobe,
  faGrid2,
  faHandHoldingHeart,
  faHeart,
  faHeartPulse,
  faKey,
  faLayerGroup,
  faLightbulb,
  faLink,
  faList,
  faLocationDot,
  faMagnifyingGlass,
  faMagnifyingGlassChart,
  faMapPin,
  faMerge,
  faMessage,
  faMessages,
  faMicrophone,
  faMoneyBill,
  faNetworkWired,
  faPalette,
  faPenToSquare,
  faPiggyBank,
  faPlug,
  faReceipt,
  faRobot,
  faRocket,
  faRotateLeft,
  faRoute,
  faShareNodes,
  faShelves,
  faShield,
  faShieldCheck,
  faSliders,
  faSparkles,
  faStore,
  faTable,
  faTag,
  faTags,
  faTriangleExclamation,
  faTruck,
  faTruckFast,
  faTruckMoving,
  faTruckRampBox,
  faUpload,
  faUserShield,
  faWandMagicSparkles,
  faWarehouse,
  faWifi,
  faWindowRestore,
} from '@crisp/crisp-ui/icons/classic/regular';
import { faMicrosoft } from '@crisp/crisp-ui/icons/classic/brands';

/**
 * Icons pages commonly use, imported up front so they render without waiting for
 * the full catalog. Anything else resolves from `virtual:icon-catalog` on demand.
 */
const COMMON_ICONS: IconDefinition[] = [
  faArrowRotateLeft,
  faArrowsLeftRight,
  faArrowsTurnToDots,
  faArrowTrendUp,
  faBadgeCheck,
  faBook,
  faBookmark,
  faBox,
  faBoxCheck,
  faBoxesStacked,
  faBrain,
  faBullseye,
  faBullseyeArrow,
  faCalendar,
  faCalendarCheck,
  faCalendarClock,
  faCalendarWeek,
  faCar,
  faCartShopping,
  faCashRegister,
  faChartArea,
  faChartBar,
  faChartLine,
  faChartMixed,
  faChartSimple,
  faChess,
  faCircleCheck,
  faCircleQuestion,
  faClipboardCheck,
  faClock,
  faComments,
  faCompass,
  faCreditCard,
  faCrystalBall,
  faCubes,
  faDatabase,
  faDiagramNested,
  faDollarSign,
  faEye,
  faFaceSmile,
  faFile,
  faFileInvoice,
  faFileLines,
  faFilePowerpoint,
  faFlag,
  faFolderTree,
  faGaugeHigh,
  faGear,
  faGears,
  faGlobe,
  faGrid2,
  faHandHoldingHeart,
  faHeart,
  faHeartPulse,
  faKey,
  faLayerGroup,
  faLightbulb,
  faLink,
  faList,
  faLocationDot,
  faMagnifyingGlass,
  faMagnifyingGlassChart,
  faMapPin,
  faMerge,
  faMessage,
  faMessages,
  faMicrophone,
  faMoneyBill,
  faNetworkWired,
  faPalette,
  faPenToSquare,
  faPiggyBank,
  faPlug,
  faReceipt,
  faRobot,
  faRocket,
  faRotateLeft,
  faRoute,
  faShareNodes,
  faShelves,
  faShield,
  faShieldCheck,
  faSliders,
  faSparkles,
  faStore,
  faTable,
  faTag,
  faTags,
  faTriangleExclamation,
  faTruck,
  faTruckFast,
  faTruckMoving,
  faTruckRampBox,
  faUpload,
  faUserShield,
  faWandMagicSparkles,
  faWarehouse,
  faWifi,
  faWindowRestore,
  faMicrosoft,
];

/** Names content uses that aren't Font Awesome names (mostly Lucide, from Mintlify). */
const LEGACY_ALIASES: Record<string, IconDefinition> = {
  layers: faLayerGroup,
  bot: faRobot,
  target: faBullseye,
  'help-circle': faCircleQuestion,
  'layout-grid': faGrid2,
  settings: faGear,
  'message-square': faMessage,
  'alert-triangle': faTriangleExclamation,
};

type SetPrefix = 'far' | 'fab' | 'fak';
type IconIndex = Record<SetPrefix, Map<string, IconDefinition>>;

function indexIcons(icons: IconDefinition[]): IconIndex {
  const index: IconIndex = { far: new Map(), fab: new Map(), fak: new Map() };
  for (const icon of icons) {
    const byName = index[icon.prefix as SetPrefix];
    if (!byName) continue;
    for (const alias of icon.icon[2]) {
      if (typeof alias === 'string' && !byName.has(alias)) byName.set(alias, icon);
    }
    byName.set(icon.iconName, icon);
  }
  return index;
}

const COMMON = indexIcons(COMMON_ICONS);

interface Catalog {
  index: IconIndex;
  icons: IconDefinition[];
}

let catalog: Catalog | null = null;
let catalogFailed = false;
let catalogRequest: Promise<Catalog> | null = null;

/** Loads every Font Awesome icon (about 4,400) in its own chunk; resolves once. */
export function loadIconCatalog(): Promise<Catalog> {
  catalogRequest ??= import('virtual:icon-catalog').then(
    ({ default: rows }) => {
      const icons = rows.map(
        ([prefix, iconName, width, height, aliases, path]) =>
          ({ prefix: prefix as IconPrefix, iconName, icon: [width, height, aliases, '', path] }) as IconDefinition
      );
      catalog = { index: indexIcons(icons), icons };
      return catalog;
    },
    (error: unknown) => {
      catalogFailed = true;
      throw error;
    }
  );
  return catalogRequest;
}

/**
 * Splits `fab:github`, `fa-brands fa-github` or `fa-github` into an optional set and
 * the names to look up: without a leading `fa-`, then as written, since one kit icon
 * is itself named `fa-cr-data-table-star`.
 */
function parseIconName(raw: string): { prefix?: SetPrefix; names: string[] } {
  let name = raw.toLowerCase().trim();
  let style = '';
  const colon = name.match(/^(fa[srbk]?):/);
  if (colon) {
    style = colon[1];
    name = name.slice(colon[0].length);
  }
  const words = name.match(/^fa-(solid|regular|brands|light|thin|duotone|kit)\s+fa-/);
  if (words) {
    style = words[1];
    name = name.slice(words[0].length);
  }
  const short = name.match(/^(fa[srbk])\s+fa-/);
  if (short) {
    style = short[1];
    name = name.slice(short[0].length);
  }
  name = name.trim();
  const names = name.startsWith('fa-') ? [name.slice(3), name] : [name];
  if (style === 'fab' || style === 'brands') return { prefix: 'fab', names };
  if (style === 'fak' || style === 'kit') return { prefix: 'fak', names };
  // Only the regular style ships, so solid, light and the rest render as regular.
  return { names };
}

/**
 * The icon a frontmatter or MDX icon name resolves to, from the common icons or,
 * once loaded, the full catalog. Undefined when it isn't known (yet).
 */
export function resolveIcon(raw: string | undefined): IconDefinition | undefined {
  if (!raw) return undefined;
  const { prefix, names } = parseIconName(raw);
  for (const name of names) {
    const find = (set: SetPrefix) => catalog?.index[set].get(name) ?? COMMON[set].get(name);
    const icon = prefix ? find(prefix) : find('far') ?? find('fab') ?? find('fak') ?? LEGACY_ALIASES[name];
    if (icon) return icon;
  }
  return undefined;
}

/** Resolves an icon name, loading the full catalog when the common icons don't cover it. */
export function useIcon(raw: string | undefined): { icon: IconDefinition | undefined; pending: boolean } {
  const [, rerender] = useReducer((count: number) => count + 1, 0);
  const icon = resolveIcon(raw);
  const pending = Boolean(raw) && !icon && !catalog && !catalogFailed;
  useEffect(() => {
    if (!pending) return undefined;
    let live = true;
    const settle = () => {
      if (live) rerender();
    };
    loadIconCatalog().then(settle, settle);
    return () => {
      live = false;
    };
  }, [pending]);
  return { icon, pending };
}

export interface IconOption {
  /** The name written to frontmatter when picked; prefixed only when a bare name means another icon. */
  name: string;
  icon: IconDefinition;
  /** Other names that find this icon when searching. */
  keywords: string[];
}

let iconOptions: IconOption[] | null = null;

/** Every icon, sorted by name, for the icon picker. */
export async function loadIconOptions(): Promise<IconOption[]> {
  if (iconOptions) return iconOptions;
  const { icons } = await loadIconCatalog();
  const legacyKeywords = new Map<string, string[]>();
  for (const [alias, icon] of Object.entries(LEGACY_ALIASES)) {
    const key = `${icon.prefix}:${icon.iconName}`;
    legacyKeywords.set(key, [...(legacyKeywords.get(key) ?? []), alias]);
  }
  iconOptions = icons
    .map((icon) => ({
      name: resolveIcon(icon.iconName) === icon ? icon.iconName : `${icon.prefix}:${icon.iconName}`,
      icon,
      keywords: [
        ...icon.icon[2].filter((alias): alias is string => typeof alias === 'string'),
        ...(legacyKeywords.get(`${icon.prefix}:${icon.iconName}`) ?? []),
      ],
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
  return iconOptions;
}

interface IconProps {
  name?: string;
  className?: string;
}

export const Icon: React.FC<IconProps> = ({ name, className }) => {
  const { icon, pending } = useIcon(name);
  if (!name) return null;
  const slotClass = className ? `icon-slot ${className}` : 'icon-slot';
  // Hold the icon's space while the catalog loads, so text doesn't shift when it arrives.
  if (pending) return <span className={slotClass} style={{ display: 'inline-block' }} aria-hidden="true" />;
  return <FontAwesomeIcon icon={icon ?? faFileLines} className={slotClass} />;
};

export default Icon;
